import React, { useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Button,
  useTheme,
} from '@mui/material';
import {
  CheckCircle,
  SportsCricket,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const Logout = () => {
  const theme = useTheme();
  const { logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const performLogout = async () => {
      await logout();
      // Redirect after a short delay to show the confirmation
      setTimeout(() => {
        navigate('/auth/login', { replace: true });
      }, 2000);
    };

    if (isAuthenticated) {
      performLogout();
    } else {
      // User is already logged out, redirect immediately
      navigate('/auth/login', { replace: true });
    }
  }, [logout, isAuthenticated, navigate]);

  const handleGoToLogin = () => {
    navigate('/auth/login', { replace: true });
  };

  const handleGoToHome = () => {
    navigate('/', { replace: true });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)'
          : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
        py: 4,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 3,
            backdropFilter: 'blur(10px)',
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)' 
              : 'rgba(255, 255, 255, 0.95)',
            textAlign: 'center',
          }}
        >
          {/* Logo and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <SportsCricket sx={{ fontSize: 40, color: 'primary.main', mr: 1 }} />
            <Typography component="h1" variant="h4" fontWeight="bold" color="primary">
              IPL Fantasy
            </Typography>
          </Box>

          {isAuthenticated ? (
            // Show loading while logging out
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <Typography variant="h5" gutterBottom>
                Logging you out...
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please wait while we securely log you out of your account.
              </Typography>
            </Box>
          ) : (
            // Show success message after logout
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <CheckCircle 
                sx={{ 
                  fontSize: 80, 
                  color: 'success.main', 
                  mb: 3,
                  animation: 'fadeIn 0.5s ease-in-out'
                }} 
              />
              <Typography variant="h4" gutterBottom color="success.main" fontWeight="bold">
                Logged Out Successfully!
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                You have been securely logged out of your IPL Fantasy account.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Thank you for playing IPL Fantasy! We hope to see you again soon.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Button
                  variant="contained"
                  onClick={handleGoToLogin}
                  sx={{
                    px: 4,
                    py: 1.5,
                    background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                    },
                  }}
                >
                  Sign In Again
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleGoToHome}
                  sx={{ px: 4, py: 1.5 }}
                >
                  Go to Home
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default Logout;